import { unsafeCSS, html } from 'lit';
import { customElement } from 'lit/decorators.js';
import { unsafeHTML } from 'lit/directives/unsafe-html.js';
import style from './calendar-year-table.scss?inline';
import { PopBaseComponent } from '../../base/pop-base-component.js';
import { Constants, RowHeaders } from './calendar-year-table.constants.js';
import type { CalendarYearTableContentObject } from './calendar-year-table.model.js';
import { formatDecimalAsPercentage } from '../../common/utils/formatNumber';
import { AssetClassEnum } from '../../common/enums/asset-class';

/**
 * @tag pop-calendar-year-table-component
 * @property {CalendarYearTableContentObject} [componentObject] CalendarYearTable content
 * @attribute { CalendarYearTableContentObject } [data-component-object] CalendarYearTable content object
 */
@customElement(Constants.TAG_NAME)
export class PopCalendarYearTableComponent extends PopBaseComponent {
  static get styles() {
    return [...super.styles, unsafeCSS(style)];
  }

  /**
   * @internal
   */
  tagName = Constants.TAG_NAME;

  /**
   * CalendarYearTable content object
   */
  componentObject?: CalendarYearTableContentObject;

  private renderTableHead() {
    const availableYears = (this.componentObject?.itemList || [])
      .map((item) => item.year)
      .filter(Boolean);

    return html`
      <tr>
        <th id="name-column" scope="col"></th>
        ${availableYears.map(
          (year) => html`
            <th id="year-${year}-column" class="right-align" scope="col">
              <beacon-table-header variant="normal">
                ${year}
              </beacon-table-header>
            </th>
          `,
        )}
      </tr>
    `;
  }

  private renderTableBody() {
    const items = this.componentObject?.itemList || [];
    const assetClass = this.componentObject?.assetClass;

    const rows = [
      {
        header:
          assetClass === AssetClassEnum.FIXED_INCOME
            ? RowHeaders?.PureGrossFixedIncome
            : RowHeaders?.PureGross,
        dataField: 'pureGross',
      },
      {
        header:
          assetClass === AssetClassEnum.FIXED_INCOME
            ? RowHeaders?.NetFixedIncome
            : RowHeaders?.Net,
        dataField: 'net',
      },
      {
        header: RowHeaders?.Benchmark,
        dataField: 'benchmark',
      },
      {
        header: RowHeaders?.ValuePureGross,
        dataField: 'pureGrossValueAdded',
      },
      {
        header: RowHeaders?.ValueNet,
        dataField: 'netValueAdded',
      },
    ];

    return html`
      ${rows.map(
        (row) => html`
          <tr>
            <th>
              <beacon-table-stub>
                <span
                  class="b-typography-sans-serif--body--100 b-font-weight-bold"
                >
                  ${unsafeHTML(row.header)}
                </span>
              </beacon-table-stub>
            </th>
            ${items.map((item) => {
              const value = item[row.dataField];
              return html`
                <td>
                  <beacon-table-record variant="numeral">
                    ${formatDecimalAsPercentage(value, 2)}
                  </beacon-table-record>
                </td>
              `;
            })}
          </tr>
        `,
      )}
    `;
  }

  render() {
    return this.renderComponent(html`
      <section class="calendar-year-table-content">
        <beacon-table variant="fixed">
          <table tabindex="0">
            <thead>
              ${this.renderTableHead()}
            </thead>
            <tbody>
              ${this.renderTableBody()}
            </tbody>
          </table>
        </beacon-table>
      </section>
    `);
  }
}
